import {
  Box,
  Card,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import dayjs, { Dayjs } from 'dayjs'
import { useIntl } from 'react-intl'
import { useMemo } from 'react'
import { messages } from '../../messages'
import { Route, LoadMetadata } from '../plannerTypes'
import { PlannerAction } from '../state/journeyPlannerReducer'
import DestinationBox from './DestinationBox'
import { formatDateISO } from '../../utils'
import { useGetCarriersQuery } from '../../../../app/redux-fetch/apiOrders'

type Service = {
  id: number
  name: string
}

type RouteSectionProps = {
  route: Route
  legIndex: number
  routeIndex: number
  loadMetadataById: Record<number, LoadMetadata>
  dispatch: React.Dispatch<PlannerAction>
  canRemove: boolean
  canSplit: boolean
  onSplitClick: () => void
  onAddDestinationClick: (destIndex: number) => void
  onEditActionsClick: (destIndex: number) => void
  tenantId: number
  transportModeId?: number
  previousRouteLastDate?: Dayjs
  services?: Service[]
}

const RouteSection = ({
  route,
  services = [],
  legIndex,
  routeIndex,
  loadMetadataById,
  dispatch,
  canRemove,
  canSplit,
  onSplitClick,
  onAddDestinationClick,
  onEditActionsClick,
  tenantId,
  transportModeId,
  previousRouteLastDate,
}: RouteSectionProps) => {
  const { formatMessage } = useIntl()

  const { data: carriers = [] } = useGetCarriersQuery(
    { tenantId },
    { skip: !tenantId }
  )

  const carrierOptions = useMemo(() => {
    if (transportModeId) {
      return carriers.filter((carrier) => carrier.transportModeId === transportModeId)
    }
    return carriers
  }, [carriers, transportModeId])

  const handleCarrierChange = (event: SelectChangeEvent<number>) => {
    const selectedCarrierId = event.target.value as number
    const selectedCarrier = carriers.find((c) => c.id === selectedCarrierId)
    dispatch({
      type: 'UPDATE_ROUTE_CARRIER',
      legIndex,
      routeIndex,
      carrierId: selectedCarrierId,
      carrierName: selectedCarrier?.name,
    })
  }

  const handleTransitIdentifierChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: 'UPDATE_ROUTE_TRANSIT_IDENTIFIER',
      legIndex,
      routeIndex,
      transitIdentifier: event.target.value,
    })
  }

  return (
    <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            {formatMessage(messages.routeNumber, { number: route.displayOrder })}
          </Typography>
          <Link
            component="button"
            variant="body2"
            onClick={onSplitClick}
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {formatMessage(messages.splitRoute)}
          </Link>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id={`carrier-label-${legIndex}-${routeIndex}`}>
              {formatMessage(messages.carrierName)}
            </InputLabel>
            <Select
              labelId={`carrier-label-${legIndex}-${routeIndex}`}
              value={route.carrierId || ''}
              label={formatMessage(messages.carrierName)}
              onChange={handleCarrierChange}
            >
              <MenuItem value="">
                <em>{formatMessage(messages.selectCarrier)}</em>
              </MenuItem>
              {carrierOptions.map((carrier) => (
                <MenuItem key={carrier.id} value={carrier.id}>
                  {carrier.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label={formatMessage(messages.transitIdentifier)}
            value={route.transitIdentifier || ''}
            onChange={handleTransitIdentifierChange}
            sx={{ minWidth: 150 }}
          />
          <Typography variant="body2" color="text.secondary">
            {formatMessage(messages.scheduledDeparture)}: {formatDateISO(route.scheduledDepartureDate) || '-'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatMessage(messages.scheduledArrival)}: {formatDateISO(route.scheduledArrivalDate) || '-'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflowX: 'auto', py: 1 }}>
          {route.destinations.map((destination, destIndex) => {
            // Calculate minDate for this destination
            // For first destination: use previousRouteLastDate or today
            // For subsequent destinations: use previous destination's date
            let minDate: Dayjs
            if (destIndex === 0) {
              minDate = previousRouteLastDate || dayjs().startOf('day')
            } else {
              const prevDest = route.destinations[destIndex - 1]
              minDate = prevDest.scheduledDate
                ? dayjs(prevDest.scheduledDate)
                : dayjs().startOf('day')
            }

            return (
            <Box key={destination.id} sx={{ display: 'flex', alignItems: 'center' }}>
              <DestinationBox
                destination={destination}
                legIndex={legIndex}
                routeIndex={routeIndex}
                destIndex={destIndex}
                loadMetadataById={loadMetadataById}
                dispatch={dispatch}
                canRemove={route.destinations.length > 2}
                onEditClick={() => onEditActionsClick(destIndex)}
                minDate={minDate}
                services={services}
              />
              {destIndex < route.destinations.length - 1 && (
                <>
                  <ArrowForwardIcon sx={{ mx: 0.5, color: 'grey.500' }} />
                  <IconButton
                    size="small"
                    onClick={() => onAddDestinationClick(destIndex)}
                    sx={{
                      color: 'primary.main',
                      p: 0,
                      '&:hover': { backgroundColor: 'transparent' },
                    }}
                    aria-label="Add destination"
                    data-testid="add-destination-button"
                  >
                    <AddCircleIcon sx={{ fontSize: '1.5em' }} />
                  </IconButton>
                </>
              )}
            </Box>
            )
          })}
        </Box>
      </CardContent>
    </Card>
  )
}

export default RouteSection
